import {
    HubConnectionBuilder,
    HttpTransportType,
    HubConnection,
} from "@microsoft/signalr";

import AppState from "../foundation/AppState";
import Configuration from "../foundation/Configuration";
import Logger from "../foundation/Logger";
import store, { IAppState } from "../state/Store";
import {
    fetchMessagesUnread,
    fetchMessagingSummary,
} from "../state/system/Messaging";

interface ThreadInfo {
    state: boolean;
    type: number;
    context?: string;
}

let connection: HubConnection = null;
let mainCallback: (data: any) => void;
let mainFailback: (error: Error) => void;

export async function InitializeMessagingHub(): Promise<boolean> {
    const jwt = await AppState.instance.getSessionJWT();

    if (!jwt) {
        throw new Error("No authentication found");
    }

    connection = new HubConnectionBuilder()
        .withUrl(`${Configuration.messagingPath}/api/Messaging/Notifications`, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => {
                return `Bearer ${jwt}`;
            },
        })
        .withAutomaticReconnect()
        .build();

    if (connection) {
        connection
            .start()
            .then(result => {
                Logger.verbose("Connected to messaging socket");

                connection.on("Broadcast", message => {
                    const colon = message.indexOf(":");
                    const parts =
                        colon < 0
                            ? [message]
                            : [
                                  message.substring(0, colon),
                                  message.substring(colon + 1),
                              ];

                    switch (parts[0]) {
                        case "message_read":
                            store.dispatch(fetchMessagesUnread());
                            break;
                        case "new_messages":
                            const { ui } = store.getState().db;
                            store.dispatch(fetchMessagesUnread());
                            if (ui.messagingOpen)
                                store.dispatch(fetchMessagingSummary());
                            break;
                        case "thread_messages":
                            if (mainCallback) mainCallback(parts[1]);
                            break;
                    }
                });

                connection.onclose(error => {
                    mainFailback(error);
                });

                connection.send(
                    "Message",
                    JSON.stringify({ type: 2, state: true } as ThreadInfo),
                );
            })
            .catch(e => Logger.error(e));

        return true;
    } else {
        return false;
    }
}

function connectionWait() {
    return new Promise(resolve => {
        let around = () => {
            if (connection.state !== "Connected") setTimeout(around, 100);
            else resolve(null);
        };
        around();
    });
}

export async function ConnectThread(
    context: string,
    callback: (data: any) => void,
    failback?: (error: Error) => void,
) {
    await connectionWait();

    if (!mainCallback) {
        mainCallback = callback;
        mainFailback = failback;

        connection.invoke(
            "Message",
            JSON.stringify({
                type: 1,
                state: true,
                context: context,
            } as ThreadInfo),
        );
    }
}

export async function DisconnectThread() {
    await connectionWait();

    mainCallback = null;
    mainFailback = null;

    connection.invoke(
        "Message",
        JSON.stringify({
            type: 1,
            state: false,
            contextId: "",
        } as ThreadInfo),
    );
}
